import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Button,
  IconButton,
  Typography,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Chip,
  Autocomplete,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PeopleIcon from '@mui/icons-material/People';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import api from '../../utils/api';
import { ListPageLayout } from '../../components/ListPage';
import { tokens } from '../../theme/tokens';

export default function HRGroups() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '' });
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [allMembersForForm, setAllMembersForForm] = useState([]);
  const [membersFormLoading, setMembersFormLoading] = useState(false);
  const [membersDialogOpen, setMembersDialogOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupMembers, setGroupMembers] = useState([]);
  const [allMembers, setAllMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [addMemberValue, setAddMemberValue] = useState(null);

  useEffect(() => {
    fetchGroups();
  }, [page, rowsPerPage]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (page === 0) {
        fetchGroups();
      } else {
        setPage(0);
      }
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [search]);

  const fetchGroups = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get('/hr/groups', {
        params: {
          page: page + 1,
          limit: rowsPerPage,
          search: search || undefined,
        },
      });
      setGroups(response.data.data || []);
      setTotal(Number(response.data.meta?.total) || 0);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch groups');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenDialog = async (group = null) => {
    setEditingGroup(group);
    setFormData({ name: group?.name || '', description: group?.description || '' });
    setSelectedMembers([]);
    setOpenDialog(true);
    if (!group) {
      try {
        setMembersFormLoading(true);
        const res = await api.get('/members', { params: { limit: 500, status: 'Active' } });
        setAllMembersForForm(res.data.data || []);
      } catch (err) {
        setAllMembersForForm([]);
      } finally {
        setMembersFormLoading(false);
      }
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingGroup(null);
    setFormData({ name: '', description: '' });
    setSelectedMembers([]);
  };

  const handleSubmit = async () => {
    try {
      if (editingGroup) {
        await api.put(`/hr/groups/${editingGroup.id}`, formData);
      } else {
        const createRes = await api.post('/hr/groups', formData);
        const newGroup = createRes.data?.data;
        if (newGroup && selectedMembers.length > 0) {
          await api.post(`/hr/groups/${newGroup.id}/members`, {
            member_ids: selectedMembers.map((m) => m.id),
          });
        }
      }
      handleCloseDialog();
      fetchGroups();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save group');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this group?')) {
      return;
    }
    try {
      await api.delete(`/hr/groups/${id}`);
      fetchGroups();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete group');
    }
  };

  const handleOpenMembersDialog = async (group) => {
    setSelectedGroup(group);
    setMembersDialogOpen(true);
    setGroupMembers([]);
    setAddMemberValue(null);
    try {
      setMembersLoading(true);
      const [membersRes, allRes] = await Promise.all([
        api.get(`/hr/groups/${group.id}/members`),
        api.get('/members', { params: { limit: 500, status: 'Active' } }),
      ]);
      setGroupMembers(membersRes.data.data || []);
      setAllMembers(allRes.data.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load members');
    } finally {
      setMembersLoading(false);
    }
  };

  const handleCloseMembersDialog = () => {
    setMembersDialogOpen(false);
    setSelectedGroup(null);
    setGroupMembers([]);
    setAddMemberValue(null);
  };

  const handleAddMember = async () => {
    if (!addMemberValue || !selectedGroup) return;
    try {
      await api.post(`/hr/groups/${selectedGroup.id}/members`, {
        member_ids: [addMemberValue.id],
      });
      setGroupMembers((prev) => [...prev, addMemberValue]);
      setAddMemberValue(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add member');
    }
  };

  const handleRemoveMember = async (member) => {
    if (!selectedGroup) return;
    try {
      await api.delete(`/hr/groups/${selectedGroup.id}/members/${member.id}`);
      setGroupMembers((prev) => prev.filter((m) => m.id !== member.id));
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to remove member');
    }
  };

  const membersNotInGroup = allMembers.filter(
    (m) => !groupMembers.some((gm) => gm.id === m.id)
  );

  return (
    <>
    <ListPageLayout
      title="Groups"
      crumbs={
        <>
          Registry / <Box component="b" sx={{ color: tokens.ink, fontWeight: 500 }}>Groups</Box>
        </>
      }
      searchPlaceholder="Search groups..."
      searchValue={search}
      onSearchChange={setSearch}
      error={error}
      onErrorClose={() => setError('')}
      actions={
        <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Add Group
          </Button>
      }
    >
      <TableContainer component={Paper} sx={{ overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Members</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {groups.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                        No records yet
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  groups.map((group) => (
                    <TableRow key={group.id} hover>
                      <TableCell>{group.name}</TableCell>
                      <TableCell>{group.description || '-'}</TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          startIcon={<PeopleIcon />}
                          onClick={() => handleOpenMembersDialog(group)}
                        >
                          Manage Members
                        </Button>
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(group)}
                          title="Edit"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(group.id)}
                          title="Delete"
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </>
        )}
      </TableContainer>
    </ListPageLayout>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingGroup ? 'Edit Group' : 'Add Group'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Name"
            fullWidth
            variant="standard"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            variant="standard"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            multiline
            rows={3}
            sx={{ mb: 2 }}
          />
          {!editingGroup && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Add members to this group
              </Typography>
              <Autocomplete
                multiple
                size="small"
                options={allMembersForForm}
                getOptionLabel={(m) => `${m.first_name || ''} ${m.last_name || ''} (${m.member_id || m.email || ''})`}
                isOptionEqualToValue={(opt, val) => opt.id === val.id}
                value={selectedMembers}
                onChange={(e, v) => setSelectedMembers(v)}
                loading={membersFormLoading}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Search and select members" />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((member, index) => {
                    const { key, ...otherTagProps } = getTagProps({ index });
                    return (
                      <Chip
                        key={key}
                        label={`${member.first_name} ${member.last_name}`}
                        {...otherTagProps}
                        size="small"
                      />
                    );
                  })
                }
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={!formData.name}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={membersDialogOpen} onClose={handleCloseMembersDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Manage Members — {selectedGroup?.name}
        </DialogTitle>
        <DialogContent dividers>
          {membersLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Add members to this group
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <Autocomplete
                  size="small"
                  options={membersNotInGroup}
                  getOptionLabel={(m) => `${m.first_name} ${m.last_name} (${m.member_id || m.email})`}
                  value={addMemberValue}
                  onChange={(e, v) => setAddMemberValue(v)}
                  renderInput={(params) => (
                    <TextField {...params} placeholder="Search member to add" />
                  )}
                  sx={{ flex: 1 }}
                />
                <Button
                  variant="contained"
                  startIcon={<PersonAddIcon />}
                  onClick={handleAddMember}
                  disabled={!addMemberValue}
                >
                  Add
                </Button>
              </Box>

              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Members in this group ({groupMembers.length})
              </Typography>
              {groupMembers.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                  No members in this group yet. Add members above.
                </Typography>
              ) : (
                <List dense sx={{ maxHeight: 280, overflow: 'auto' }}>
                  {groupMembers.map((member) => (
                    <ListItem
                      key={member.id}
                      secondaryAction={
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveMember(member)}
                          title="Remove from group"
                          color="error"
                        >
                          <PersonRemoveIcon fontSize="small" />
                        </IconButton>
                      }
                    >
                      <ListItemText
                        primary={`${member.first_name} ${member.last_name}`}
                        secondary={member.email || member.phone || member.member_id}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseMembersDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
